import { FC, useEffect, useState } from 'react';
import { useCreateDailyLogMutation, useDailyLogQuery } from '../../graphql/generated/operations/dailyLog.ts';
import { DailyLogInput } from '../../graphql/generated/graphql.ts';
import { LoadingBackdrop } from '../../components/loading.backdrop.tsx';
import { format, parseISO } from 'date-fns';
import { Paths } from '../../constants/paths.ts';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../contexts/snackbar.context.tsx';
import { ActivityReportPage } from './steps/activity-report.page.tsx';
import { Questions } from './steps/questions.page.tsx';
import { trackEvent } from '../../utils/analytics.ts';

export const DailyLogPage: FC = () => {
  const { data, loading } = useDailyLogQuery({
    variables: {
      offset: Math.floor(new Date().getTimezoneOffset() / 60) // Timezone offset in hours
    }
  });
  const [submitDailyLog, { data: dailyLog, loading: submitting }] = useCreateDailyLogMutation();
  const [input, setInput] = useState<Partial<DailyLogInput>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [DAY, setDay] = useState<string>('');
  const [skipActivityReport, setSkipActivityReport] = useState<boolean>(false);
  const navigate = useNavigate();
  const { push } = useSnackbar();

  useEffect(() => {
    if (activeStep === 2 || (activeStep === 1 && data?.dailyLogActivities.length === 0)) {
      trackEvent('daily_log_submit');
      input.offset = Math.floor(new Date().getTimezoneOffset() / 60);
      submitDailyLog({
        variables: {
          input: input as DailyLogInput
        }
      });
    }
  }, [activeStep]);

  useEffect(() => {
    if (data?.needToAnswerDailyLog === false) {
      trackEvent('daily_log_already_submitted');
      push({ type: 'info', message: 'Daily log already submitted' });
      navigate(Paths.HOME);
    }

    if (data?.dailyLogActivities.length === 0) {
      setSkipActivityReport(true);
    }

    if (data?.dailyLogDate) {
      setDay(format(parseISO(data.dailyLogDate), 'EEEE'));
    }
  }, [data]);

  useEffect(() => {
    if (dailyLog) {
      trackEvent('daily_log_success');
      push({ type: 'success', message: 'Daily log submitted' });
      navigate(Paths.HOME);
    }
  }, [dailyLog]);

  if (loading || submitting) {
    return <LoadingBackdrop />;
  }

  if (activeStep === 0) {
    return (
      <>
        <Questions
          DAY={DAY}
          skipActivityReport={skipActivityReport}
          questions={data?.questions as any}
          onNext={(newInput) => {
            setInput(newInput);
            setActiveStep(1);
            trackEvent('daily_log_questions_complete');
          }}
        />
      </>
    );
  }
  if (activeStep === 1 && data?.dailyLogActivities && data?.dailyLogActivities.length > 0) {
    return (
      <>
        <ActivityReportPage
          activityLog={data?.dailyLogActivities as any[]}
          onNext={() => {
            setActiveStep(2);
            trackEvent('daily_log_activity_report_complete');
          }}
        />
      </>
    );
  }
};
